<div
    [ngClass]="{ 'panel panel-default vertical-container': flightId == null }"
    style="overflow: hidden; margin-bottom: 10px"
    ngfDrop
    [(validDrag)]="baseDropValid"
    [class.invalid-drag]="baseDropValid === false"
    [class.valid-drag]="baseDropValid"
    accept="*/*, .log"
    [(files)]="uploadingFlightLogs"
    (filesChange)="uploadLogs($event)"
    [fileDropDisabled]="working || readonly || isUploading"
>
    <!-- If no flightId, show panel heading with buttons -->
    <div *ngIf="flightId == null" class="panel-heading">
        <div class="horizontal-container text-centered">
            <span class="display-label">Unassigned Flight Logs</span>
        </div>
        <div class="horizontal-container" style="flex-wrap: wrap; row-gap: 3px">
            <!-- Attach logs button -->
            <button
                type="button"
                class="btn btn-pill btn-xs"
                tooltip="Attach flight logs from the organisation's unassigned flight log list to this mission."
                placement="right"
                (click)="attachLogFiles()"
                [disabled]="working || readonly"
                style="width: 78px"
            >
                Attach Logs
            </button>
            <!-- Create flights button if there are logs -->
            <button
                *ngIf="hasLogs"
                type="button"
                class="btn btn-pill btn-xs"
                [ngClass]="{
                    'btn-warn': hasLogsMatchingFlights
                }"
                [tooltip]="
                    hasLogsMatchingFlights
                        ? 'Create flights for each flight log in the list and assign the logs. WARNING: Sone flight logs in the list match existing flights. Click \'Attach To Flights\' first if you wish to attach flight logs to matching flights.'
                        : 'Create flights for each flight log in the list and assign the logs.'
                "
                placement="right"
                [disabled]="working || readonly || mission.id == null"
                (click)="createFlightsFromLogs()"
                style="width: 90px"
            >
                Create Flights
            </button>
            <button
                *ngIf="hasLogs && hasLogsMatchingFlights"
                type="button"
                class="btn btn-pill btn-xs"
                [tooltip]="
                    hasLogsMatchingFlights
                        ? 'Add all flight logs that match existing flights to the first matching flight on the mission.'
                        : 'No existing flights match any of the flight logs in this list'
                "
                placement="right"
                [disabled]="working || readonly || mission.id == null"
                (click)="assignAllLogsMatchingFlights()"
            >
                Add All To Flights
            </button>
            <button
                type="button"
                class="btn btn-pill btn-xs"
                tooltip="Upload a new flight log file that hasn't been uploaded to FlyFreely yet. To add a previously uploaded flight log to the mission, use the \'Attach Logs\' button"
                placement="right"
                [disabled]="working || readonly || isUploading"
                ngfSelect
                accept="*/*,.log,.txt"
                multiple="1"
                [(files)]="uploadingFlightLogs"
                (filesChange)="uploadLogs($event)"
                style="width: 86px"
            >
                Upload Logs
            </button>
        </div>
    </div>

    <!-- Panel body for displaying flight logs with scrollable container -->
    <div
        [ngClass]="{
            'panel-body container-scroll modal-scrollbar': flightId == null
        }"
    >
        <!-- Assigned flight logs label if a flight is selected -->
        <div class="display-label" *ngIf="flightId != null">
            Assigned Flight Logs
            <button
                type="button"
                class="btn btn-pill btn-xs"
                tooltip="Upload a new flight log file to this flight that hasn't been uploaded to FlyFreely yet."
                placement="right"
                [disabled]="working || readonly || isUploading"
                ngfSelect
                accept="*/*,.log,.txt"
                multiple="1"
                [(files)]="uploadingFlightLogs"
                (filesChange)="uploadLogs($event)"
                style="width: 86px"
            >
                Upload Logs
            </button>
        </div>

        <!-- Replacing sortablejs with Angular CDK drop list -->
        <!-- cdkDropList is used to enable drag-and-drop reordering -->
        <ng-container *ngIf="flightLogs$ | async as flightLogs">
            <!-- Updated the cdkDropListDropped to use the template variable 'flightLogs' -->
            <div
                id="flightLogContainer{{ log }}"
                cdkDropList
                (cdkDropListDropped)="drop($event, flightLogs)"
                [cdkDropListData]="flightLogs"
                [ngClass]="{
                    'well top-buffer': flightId != null
                }"
            >
                <!-- Using flightLogs (from async pipe) inside the *ngFor loop to iterate through the logs -->
                <div
                    *ngFor="
                        let log of flightLogs;
                        let i = index;
                        let first = first;
                        let last = last
                    "
                    cdkDrag
                    [ngClass]="{
                        'top-buffer': !first
                    }"
                    [attr.data-id]="log.id"
                >
                    <div
                        class="btn btn-xs btn-pill"
                        style="cursor: default"
                        [ngStyle]="{
                            'white-space':
                                flightId == null ? 'normal' : 'nowrap'
                        }"
                    >
                        <span
                            [innerHtml]="
                                log | formatFlightLog: log.rpa:mission?.timeZone
                            "
                        ></span>

                        <button
                            type="button"
                            class="btn btn-tertiary btn-sm"
                            [disabled]="working || readonly"
                            (click)="visualiseLog(log)"
                            tooltip="Visualise log file"
                            placement="right"
                        >
                            <span class="fal fa-map"></span>
                        </button>

                        <!-- Indicates this unassigned log's time (and RPA, if set) matches an existing flight on the mission -->
                        <span
                            *ngIf="
                                log.flightId == null &&
                                (log | matchesExistingFlight: mission.sorties)
                            "
                            class="fal fa-plane-circle-check text-success"
                            tooltip="Matches the time of an existing flight on this mission"
                            placement="right"
                        ></span>

                        <!-- Conditionally rendering 'Unassign' button if log.flightId is not null -->
                        <button
                            *ngIf="log.flightId != null"
                            type="button"
                            class="btn btn-link btn-sm"
                            [disabled]="working || readonly"
                            (click)="unassignFlightLog(log)"
                            tooltip="Unassign from flight"
                            placement="right"
                        >
                            Unassign
                        </button>

                        <div class="horizontal-container">
                            <!-- Conditionally rendering 'Assign' button if log.flightId is null -->
                            <button
                                *ngIf="log.flightId == null"
                                type="button"
                                class="btn btn-link btn-xs"
                                [disabled]="
                                    working || readonly || log.rpa == null
                                "
                                #popover="bs-popover"
                                [popover]="popoverTemplate"
                                [popoverContext]="{
                                    logId: log.id,
                                    flightLog: log
                                }"
                                popoverTitle="Select A Flight"
                                [tooltip]="
                                    log.rpa
                                        ? 'Assign log file to a flight'
                                        : 'Flight log needs to have an RPA assigned first'
                                "
                                placement="right"
                            >
                                Assign
                            </button>

                            <!-- Conditionally rendering 'Remove' button if flightId is null -->
                            <button
                                *ngIf="flightId == null"
                                type="button"
                                class="btn btn-link btn-xs"
                                [disabled]="working || readonly"
                                (click)="unattachFlightLog(log)"
                                tooltip="Unattach log file"
                                placement="right"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Display message when there are no flight logs -->
                <div *ngIf="flightLogs.length === 0">No flight logs</div>
            </div>
        </ng-container>

        <!-- Loading template -->
        <ng-template #loading>
            <tr>
                <td colspan="4">Loading...</td>
            </tr>
        </ng-template>
    </div>
</div>

<!-- Popover for assigning a log to a flight -->
<ng-template #popoverTemplate let-logId="logId" let-flightLog="flightLog">
    <assign-to-flight-dialogue
        [historicalMission]="historicalMission"
        [flightLog]="flightLog"
        (assignToFlight)="assignLogFiles($event, logId)"
        (closePopover)="onClosePopover()"
    >
    </assign-to-flight-dialogue>
</ng-template>
